import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { getActivityLogs } from '../../services/activity.service';
import { getActivityDisplay } from '../../constants/activityTypes';
import { decodeHTMLEntities } from '../../utils/htmlDecoder';

/**
 * Planning Activity Timeline Component with Search and Load All
 * Shows equipment planning history with search functionality and ability to load all activities at once
 */
const PlanningActivityTimeline = ({ jobId, siteId, refreshTrigger, onViewHistory }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const ITEMS_PER_PAGE = 20;

  // Get icon and color for activity type using centralized configuration
  const getActivityIcon = (actionType) => {
    const display = getActivityDisplay(actionType);
    const IconComponent = display.icon;
    return {
      icon: <IconComponent size={16} />,
      color: display.color
    };
  };

  const fetchActivities = async (isLoadMore = false, search = null) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Use provided search term or current searchTerm state
      const currentSearch = search !== null ? search : searchTerm;

      console.log('🔄 Fetching activities with options:', {
        jobId,
        siteId,
        actionTypes: ['EQUIPMENT_PLANNED', 'EQUIPMENT_PLAN_UPDATED', 'EQUIPMENT_PLAN_DELETED', 'EQUIPMENT_INSTALLED', 'EQUIPMENT_CHECKED_OUT', 'EQUIPMENT_ORDERED', 'EQUIPMENT_TRANSFERRED', 'EQUIPMENT_RECEIVED', 'EQUIPMENT_ASSIGNED', 'EQUIPMENT_RETURNED', 'EQUIPMENT_RETURNED_TO_JOB', 'EQUIPMENT_MAINTENANCE', 'EQUIPMENT_ARCHIVED', 'STATUS_CHANGED', 'NOTE_ADDED'],
        limit: ITEMS_PER_PAGE,
        offset: isLoadMore ? offset : 0,
        searchTerm: currentSearch
      });

      const response = await getActivityLogs({
        jobId: jobId,
        siteId: siteId, // Filter by site if provided
        actionTypes: ['EQUIPMENT_PLANNED', 'EQUIPMENT_PLAN_UPDATED', 'EQUIPMENT_PLAN_DELETED', 'EQUIPMENT_INSTALLED', 'EQUIPMENT_CHECKED_OUT', 'EQUIPMENT_ORDERED', 'EQUIPMENT_TRANSFERRED', 'EQUIPMENT_RECEIVED', 'EQUIPMENT_ASSIGNED', 'EQUIPMENT_RETURNED', 'EQUIPMENT_RETURNED_TO_JOB', 'EQUIPMENT_MAINTENANCE', 'EQUIPMENT_ARCHIVED', 'STATUS_CHANGED', 'NOTE_ADDED'],
        limit: ITEMS_PER_PAGE,
        offset: isLoadMore ? offset : 0,
        searchTerm: currentSearch
      });
      
      console.log('📊 Activities response:', {
        success: response.success,
        dataLength: response.data?.length || 0,
        total: response.total
      });
      
      const newActivities = response.data || [];
      
      if (isLoadMore) {
        setActivities(prev => [...prev, ...newActivities]);
        setOffset(prev => prev + ITEMS_PER_PAGE);
      } else {
        setActivities(newActivities);
        setOffset(ITEMS_PER_PAGE);
      }
      
      // Check if there are more items to load
      setHasMore(newActivities.length === ITEMS_PER_PAGE);
      
    } catch (error) {
      console.error('Error fetching planning activities:', error);
      if (!isLoadMore) {
        setActivities([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      console.log('🔄 PlanningActivityTimeline useEffect triggered - jobId:', jobId, 'siteId:', siteId, 'refreshTrigger:', refreshTrigger);
      fetchActivities(false);
    }
  }, [jobId, siteId, refreshTrigger]);

  // Debounced search effect - triggers 500ms after user stops typing
  useEffect(() => {
    if (!jobId) return;

    const timeoutId = setTimeout(() => {
      console.log('🔍 Debounced search triggered with term:', searchTerm);
      fetchActivities(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleLoadMore = async () => {
    try {
      setLoadingMore(true);

      // Fetch ALL remaining activities by using a very large limit
      const response = await getActivityLogs({
        jobId: jobId,
        siteId: siteId,
        actionTypes: ['EQUIPMENT_PLANNED', 'EQUIPMENT_PLAN_UPDATED', 'EQUIPMENT_PLAN_DELETED', 'EQUIPMENT_INSTALLED', 'EQUIPMENT_CHECKED_OUT', 'EQUIPMENT_ORDERED', 'EQUIPMENT_TRANSFERRED', 'EQUIPMENT_RECEIVED', 'EQUIPMENT_ASSIGNED', 'EQUIPMENT_RETURNED', 'EQUIPMENT_RETURNED_TO_JOB', 'EQUIPMENT_MAINTENANCE', 'EQUIPMENT_ARCHIVED', 'STATUS_CHANGED', 'NOTE_ADDED'],
        limit: 10000, // Large limit to get all remaining activities
        offset: offset,
        searchTerm: searchTerm
      });

      const newActivities = response.data || [];

      // Add all remaining activities at once
      setActivities(prev => [...prev, ...newActivities]);

      // No more activities to load
      setHasMore(false);

    } catch (error) {
      console.error('Error loading all activities:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Server-side search is now enabled - no need for client-side filtering
  // Search is handled by the backend API with searchTerm parameter

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading planning activity...
        </Typography>
      </Paper>
    );
  }

  if (activities.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          No planning activity yet. Add equipment above to see planning history.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ maxHeight: 600, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Search Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search activities by user, description, or type..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={() => setSearchTerm('')}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Activity List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activities.length === 0 && searchTerm ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No activities match your search "{searchTerm}"
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {activities.map((activity, index) => {
          const activityStyle = getActivityIcon(activity.actionType);

          // Extract equipment_id from description if available
          // Descriptions like "checked out 02087001-3 to..." or "received WMPL-1 - ..."
          const extractEquipmentId = (desc) => {
            if (!desc) return null;
            // Match patterns like "02087001-3", "WMPL-1", etc. (alphanumeric with hyphens)
            const matches = desc.match(/\b([A-Z0-9]+-\d+|[A-Z]{2,}\d+-\d+)\b/i);
            return matches ? matches[1] : null;
          };

          const equipmentId = extractEquipmentId(activity.description);
          const hasEquipment = equipmentId && onViewHistory;

          return (
            <ListItem
              key={activity.id}
              onClick={() => {
                if (hasEquipment) {
                  // Create a minimal item object with equipment_id for QuickActions
                  onViewHistory({ equipment_id: equipmentId, id: equipmentId });
                }
              }}
              sx={{
                borderBottom: index < activities.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                px: 2,
                py: 1.5,
                cursor: hasEquipment ? 'pointer' : 'default',
                '&:hover': {
                  bgcolor: hasEquipment ? 'action.hover' : 'transparent'
                }
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: `${activityStyle.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  flexShrink: 0,
                  color: activityStyle.color
                }}
              >
                {activityStyle.icon}
              </Box>
              <ListItemText
                primaryTypographyProps={{ component: "div" }}
                secondaryTypographyProps={{ component: "div" }}
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      <strong>{activity.userName}</strong> {decodeHTMLEntities(activity.description)}
                    </Typography>
                    {activity.actionType && (
                      <Chip
                        label={getActivityDisplay(activity.actionType).label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          backgroundColor: getActivityDisplay(activity.actionType).color,
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>
                }
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(activity.timestamp || activity.createdAt).toLocaleString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                  {activity.notes && (
                    <>
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Chip 
                        label="Note" 
                        size="small" 
                        variant="outlined" 
                        sx={{ height: 16, fontSize: '0.625rem' }}
                      />
                    </>
                  )}
                </Box>
              }
              />
            </ListItem>
          );
        })}
      </List>
        )}

        {hasMore && !searchTerm && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              size="small"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : <HistoryIcon />}
            >
              {loadingMore ? 'Loading All Activities...' : 'Load All Activities'}
            </Button>
          </Box>
        )}

        {!hasMore && activities.length >= ITEMS_PER_PAGE && (
          <Box sx={{ textAlign: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {searchTerm ? `Showing ${activities.length} matching activities` : 'All activities loaded'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PlanningActivityTimeline;